import { OpenapiCasinoBlueprintGetGamesResponse200Type } from 'src/domains/casino/api/generated/providers/openapi_casino_blueprint_getGames';
import { OpenapiCasinoEvolutionGetGamesResponse200Type } from 'src/domains/casino/api/generated/providers/openapi_casino_evolution_getGames';
import { OpenapiCasinoEzugiGetGamesResponse200Type } from 'src/domains/casino/api/generated/providers/openapi_casino_ezugi_getGames';
import { OpenapiCasinoLeapGetGamesResponse200Type } from 'src/domains/casino/api/generated/providers/openapi_casino_leap_getGames';
import { OpenapiCasinoOldPragmaticGetGamesResponse200Type } from 'src/domains/casino/api/generated/providers/openapi_casino_old_pragmatic_getGames';
import { OpenapiCasinoPragmaticGetGamesResponse200Type } from 'src/domains/casino/api/generated/providers/openapi_casino_pragmatic_getGames';
import { OpenapiCasinoRelaxGetGamesResponse200Type } from 'src/domains/casino/api/generated/providers/openapi_casino_relax_getGames';
import { OpenapiCasinoSgGetGamesResponse200Type } from 'src/domains/casino/api/generated/providers/openapi_casino_sg_getGames';
import {
    BLUEPRINT_NAME,
    CollectionType,
    EVOLUTION_NAME,
    EZUGI_NAME,
    LEAP_NAME,
    PRAGMATIC_NAME,
    ProviderGameType,
    RELAX_NAME,
    SG_DIGITAL_NAME,
} from 'src/domains/casino/models/models';
import { applyStudioNameOverride, convertKindToCollection, convertStudioName } from 'src/domains/casino/utils/utils';
import config from 'src/config';
import { ApiGetGamesResponse200Type } from 'src/api_openapi/v2/casino-store/apiGetGames';
import { casinoProviderUtils } from 'src/domains/casino/utils/CasinoProviderUtils';

export const blueprintToProviderGamesModel = (
    blueprintData: OpenapiCasinoBlueprintGetGamesResponse200Type
): Array<ProviderGameType> => {
    const games: Array<ProviderGameType> = blueprintData.map((game) => ({
        launchId: game.id,
        name: game.name,
        provider: casinoProviderUtils.convertNameToSlug(BLUEPRINT_NAME),
        providerName: BLUEPRINT_NAME,
        collection: convertKindToCollection(game.kind),
        studio: {
            id: game.supplier,
            name: convertStudioName(game.supplier, BLUEPRINT_NAME),
        },
        imageUrl: '',
        demo: false,
    }));
    return games;
};

export const evolutionToProviderGamesModel = (
    evolutionData: OpenapiCasinoEvolutionGetGamesResponse200Type
): Array<ProviderGameType> => {
    const games: Array<ProviderGameType> = evolutionData.map((game) => ({
        launchId: game.id,
        name: game.name,
        provider: casinoProviderUtils.convertNameToSlug(EVOLUTION_NAME),
        providerName: EVOLUTION_NAME,
        collection: convertKindToCollection(game.kind),
        studio: {
            id: game.supplier ?? EVOLUTION_NAME.toLowerCase(),
            name: convertStudioName(game.supplier, EVOLUTION_NAME),
        },
        imageUrl: '',
        demo: false,
    }));
    return games;
};

export const ezugiToProviderGamesModel = (
    ezugiData: OpenapiCasinoEzugiGetGamesResponse200Type
): Array<ProviderGameType> => {
    const games: Array<ProviderGameType> = ezugiData.map((game) => ({
        launchId: game.id,
        name: game.name,
        providerName: EZUGI_NAME,
        provider: casinoProviderUtils.convertNameToSlug(EZUGI_NAME),
        collection: convertKindToCollection(game.kind),
        studio: {
            id: EZUGI_NAME.toLowerCase(),
            name: EZUGI_NAME,
        },
        imageUrl: '',
        demo: false,
    }));
    return games;
};

export const leapToProviderGamesModel = (
    leapData: OpenapiCasinoLeapGetGamesResponse200Type
): Array<ProviderGameType> => {
    const games: Array<ProviderGameType> = leapData.map((game) => ({
        launchId: game.id,
        name: game.name,
        providerName: LEAP_NAME,
        provider: casinoProviderUtils.convertNameToSlug(LEAP_NAME),
        collection: convertKindToCollection(game.kind),
        studio: {
            id: game.supplier ?? LEAP_NAME.toLowerCase(),
            name: convertStudioName(game.supplier, LEAP_NAME),
        },
        imageUrl: '',
        demo: game.hasDemoMode,
    }));
    return games;
};

export const pragmaticToProviderGamesModel = (
    pragmaticData: OpenapiCasinoPragmaticGetGamesResponse200Type
): Array<ProviderGameType> => {
    const games: Array<ProviderGameType> = [];
    for (const game of pragmaticData) {
        if (game.kind !== 'Bingo') {
            games.push({
                launchId: game.id,
                name: game.name,
                providerName: PRAGMATIC_NAME,
                provider: casinoProviderUtils.convertNameToSlug(PRAGMATIC_NAME),
                collection: convertKindToCollection(game.kind),
                studio: {
                    id: game.supplier,
                    name: convertStudioName(game.supplier, PRAGMATIC_NAME),
                },
                imageUrl: `${config.pragmaticGameImagesUri}${game.id}.png`,
                demo: game.hasDemoMode,
            });
        }
    }
    return games;
};

export const relaxToProviderGamesModel = (
    relaxData: OpenapiCasinoRelaxGetGamesResponse200Type
): Array<ProviderGameType> => {
    const games: Array<ProviderGameType> = relaxData.map((game) => ({
        launchId: game.id,
        name: game.name,
        provider: casinoProviderUtils.convertNameToSlug(RELAX_NAME),
        providerName: RELAX_NAME,
        collection: convertKindToCollection(game.kind),
        studio: {
            id: game.supplier ?? RELAX_NAME.toLowerCase(),
            name: applyStudioNameOverride(convertStudioName(game.supplier, RELAX_NAME)),
        },
        imageUrl: '',
        demo: false,
    }));
    return games;
};

export const sgDigitalToProviderGamesModel = (
    sgDigitalData: OpenapiCasinoSgGetGamesResponse200Type
): Array<ProviderGameType> => {
    const games: Array<ProviderGameType> = sgDigitalData.map((game) => ({
        launchId: game.id,
        name: game.name,
        provider: casinoProviderUtils.convertNameToSlug(SG_DIGITAL_NAME),
        providerName: SG_DIGITAL_NAME,
        collection: convertKindToCollection(game.kind),
        studio: {
            id: game.supplier ?? SG_DIGITAL_NAME.toLowerCase(),
            name: convertStudioName(game.supplier, SG_DIGITAL_NAME),
        },
        imageUrl: '',
        demo: false,
    }));
    const filterSgDigitalStudios = (games: Array<ProviderGameType>): Array<ProviderGameType> => {
        const studiosToRemoveFromSGDigital = [
            'aristocrat',
            'evolution-gaming',
            'games-warehouse',
            'getbet',
            'hungry-bear',
            'merkur-games',
            'nazionale-elettronica',
            'nektan',
            'northern-lights',
            'openbet',
            'r-franco',
            'reel-nrg',
            'saber-interactive',
            'storm-gaming',
            'pragmatic-play',
        ];
        return games.filter((game) => {
            const studioToRemove = studiosToRemoveFromSGDigital.find((studio) => studio === game.studio.id);
            return game.studio.id !== studioToRemove;
        });
    };
    return filterSgDigitalStudios(games);
};

// --

const convertOldPragmaticCollection = (typeDescription: string): CollectionType => {
    if (typeDescription === 'Live games') {
        return 'live-casino';
    }
    if (typeDescription === 'VirtualSports') {
        return 'virtuals';
    }
    return 'casino';
};

export const oldPragmaticToProviderGamesModel = (
    pragmaticData: OpenapiCasinoOldPragmaticGetGamesResponse200Type
): Array<ProviderGameType> => {
    const games: Array<ProviderGameType> = pragmaticData.gameList.map((game) => ({
        launchId: game.gameID,
        name: game.gameName,
        provider: casinoProviderUtils.convertNameToSlug(PRAGMATIC_NAME),
        providerName: PRAGMATIC_NAME,
        collection: convertOldPragmaticCollection(game.typeDescription),
        studio: {
            id: PRAGMATIC_NAME.toLowerCase(),
            name: PRAGMATIC_NAME,
        },
        imageUrl: `${config.pragmaticGameImagesUri}${game.gameID}.png`,
        demo: game.demoGameAvailable,
    }));
    return games;
};

export const convertNewGameModelToLegacy = (newModel: ApiGetGamesResponse200Type): Array<ProviderGameType> => {
    const games: Array<ProviderGameType> = [];

    for (const game of newModel) {
        games.push({
            launchId: game.id.toString(),
            name: game.name,
            provider: game.providerSlug,
            providerName: game.providerName,
            collection: game.collectionSlug,
            studio: {
                id: game.studioId.toString(),
                name: game.studioName,
            },
            // TODO: image to do
            imageUrl: '',
            demo: game.demoAvailable,
            id: game.id,
        });
    }

    return games;
};
